/**
 * Authentication Hook
 *
 * Provides access to authentication state and methods throughout the app.
 * This is the primary interface for components to interact with auth.
 *
 * Usage:
 *   const { isAuthenticated, user, login, logout, hasPermission } = useAuth()
 */

import { useAuthStore } from '../stores/authStore'
import { type User } from '../types'

interface AuthContextType {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean
  /** Whether the user needs to complete 2FA setup */
  requires2faSetup: boolean
  /** Current user object with RBAC data, or null if not authenticated */
  user: User | null
  /** Whether an auth operation is in progress */
  isLoading: boolean
  /** Whether initial session validation has completed */
  isInitialized: boolean
  /** Login with email/password and optional 2FA token */
  login: (email: string, password: string, totpToken?: string) => Promise<User>
  /** Register a new user */
  register: (email: string, password: string, name?: string) => Promise<User>
  /** Logout current user */
  logout: () => Promise<void>
  /** Refresh auth state from storage */
  refreshAuth: () => void
  /** Initialize auth state (call on app start) */
  initialize: () => Promise<void>
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean
  /** Check if user has a specific role */
  hasRole: (roleSlug: string) => boolean

  setRequires2FASetup: (required: boolean) => void
}

export function useAuth(): AuthContextType {
  return useAuthStore()
}
